// deps
import React, { memo } from "react";

// internal-deps
import { Image, useEffectOnce, getCloudinaryUrl, LottieAnimation } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";

// helpers
import { getCloudinaryRawUrl, TEAM_LINE } from "@swiggy-private/js-utils";

// styles
import {
    DataContainer,
    CtaContainer,
    TitleContainer,
    SubTextContainer,
    TitleText,
    Subtitle,
    LottieContainer,
} from "./styles";

//interfaces
import { IHalfCardAwareness } from "@payment-ui/interfaces";

// constants
import { DEFAULT_HALF_CARD_DATA, HALF_CARD_TITLE_IMAGE, HALF_CARD_POWERED_BY_LOGOS_IMAGE } from "./constants";

// test-ids
import { INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface IProps {
    onLoad: VoidFunction;
    onCtaClick: VoidFunction;
    isOpen: boolean;
    halfCardData: IHalfCardAwareness | undefined;
}

const InAppUpiAwarenessHalfCard = (props: IProps) => {
    const { onCtaClick, onLoad, isOpen, halfCardData = DEFAULT_HALF_CARD_DATA } = props;

    // consuming v2 for few properties after design changes
    const {
        iconUrlV2: iconUrl = DEFAULT_HALF_CARD_DATA.iconUrlV2,
        titleV2: title = DEFAULT_HALF_CARD_DATA.titleV2,
        ctaV2: cta = DEFAULT_HALF_CARD_DATA.ctaV2,
        subTextV2: subText = DEFAULT_HALF_CARD_DATA.subTextV2,
        subtitle = DEFAULT_HALF_CARD_DATA.subtitle,
        lottiePath = DEFAULT_HALF_CARD_DATA.lottiePath,
        subTextLogosUrl = DEFAULT_HALF_CARD_DATA.subTextLogosUrl,
    } = halfCardData;

    useEffectOnce(() => {
        if (isOpen) onLoad();
    });

    const titleImage = getCloudinaryUrl(iconUrl, {
        width: HALF_CARD_TITLE_IMAGE.width * 2,
        height: HALF_CARD_TITLE_IMAGE.height * 2,
    });

    const subTextImage = getCloudinaryRawUrl(subTextLogosUrl, {
        height: HALF_CARD_POWERED_BY_LOGOS_IMAGE.height * 2,
        width: HALF_CARD_POWERED_BY_LOGOS_IMAGE.width * 2,
    });

    const lottieFile = getCloudinaryRawUrl(lottiePath, {}, { teamLine: TEAM_LINE.PORTAL });

    return (
        <DataContainer data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.CONTAINER}>
            <TitleContainer>
                <TitleText
                    variant="V2.heading2.neutral.bold"
                    color="text_Highest_Emphasis"
                    data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.TITLE}
                >
                    {title}
                </TitleText>

                <Image
                    imageUrl={titleImage}
                    width={HALF_CARD_TITLE_IMAGE.width}
                    height={HALF_CARD_TITLE_IMAGE.height}
                    data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.IMAGE}
                />
            </TitleContainer>
            <Subtitle
                variant="V2.body2.neutral.medium"
                color="text_Low_Emphasis"
                data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.SUBTITLE}
            >
                {subtitle}
            </Subtitle>
            <LottieContainer data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.LOTTIE}>
                <LottieAnimation
                    visible={true}
                    playLottie={true}
                    playIfVisible={true}
                    backwardSpeed={1}
                    forwardSpeed={1}
                    fileName={lottieFile}
                />
            </LottieContainer>
            <CtaContainer data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.CTA}>
                <FullWidthButton variant="specialPositive" onClick={onCtaClick}>
                    {cta}
                </FullWidthButton>
            </CtaContainer>
            <SubTextContainer data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.SUBTEXT}>
                <Text variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                    {subText}
                </Text>
                <Image
                    imageUrl={subTextImage}
                    height={HALF_CARD_POWERED_BY_LOGOS_IMAGE.height}
                    width={HALF_CARD_POWERED_BY_LOGOS_IMAGE.width}
                    data-testid={INAPP_UPI_AWARENESS_HALFCARD_TEST_IDS.POWERED_BY_IMAGE}
                />
            </SubTextContainer>
        </DataContainer>
    );
};

export default memo(InAppUpiAwarenessHalfCard);
